import { useEffect, useRef } from 'react';
import { readCompass } from './bearing';
import { gnssState, gnssLive, subscribeGnss } from './gnss';
import { geoDist, mPerLat, mPerLon } from './geo';
import { bearingDeg } from './stake';
import { spread } from './useHeading';

// photo with the viewing-direction badge burnt in (for saving / sharing the big picture)
export function stampDir(m, t) {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
      const g = c.getContext('2d'); g.drawImage(img, 0, 0);
      if (m.dir != null) {
        const R = Math.max(40, Math.min(c.width, c.height) * 0.09), x = c.width - R * 1.4, y = R * 1.4;
        g.fillStyle = 'rgba(15,23,42,0.6)'; g.strokeStyle = '#fff'; g.lineWidth = R * 0.1;
        g.beginPath(); g.arc(x, y, R, 0, 7); g.fill(); g.stroke();
        g.save(); g.translate(x, y); g.rotate((m.dir * Math.PI) / 180);
        g.beginPath(); g.moveTo(0, -R * 0.85); g.lineTo(R * 0.5, R * 0.55); g.lineTo(0, R * 0.3); g.lineTo(-R * 0.5, R * 0.55); g.closePath();
        g.fillStyle = '#f97316'; g.fill(); g.lineWidth = R * 0.06; g.stroke(); g.restore();
        g.font = `bold ${Math.round(R * 0.32)}px sans-serif`; g.fillStyle = '#fff'; g.textAlign = 'center'; g.fillText('N', x, y - R - R * 0.12);
        g.lineJoin = 'round'; const txt = dirText(t, m); g.font = `bold ${Math.round(R * 0.38)}px sans-serif`; g.textAlign = 'right';
        g.lineWidth = R * 0.12; g.strokeStyle = 'rgba(0,0,0,0.7)'; g.strokeText(txt, c.width - R * 0.4, y + R + R * 0.7); g.fillText(txt, c.width - R * 0.4, y + R + R * 0.7);
      }
      res(c.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => res(m.data);
    img.src = m.data;
  });
}
export const norm = (d) => ((Math.round(d * 2) / 2 % 360) + 360) % 360;
export const dirCard = (t, d) => (t.phCard || 'N,NE,E,SE,S,SW,W,NW').split(',')[Math.round(norm(d) / 45) % 8];
export const dirText = (t, p) => (p?.dir == null ? '' : `${t.phDir || 'Blickrichtung'} ${norm(p.dir)}° ${dirCard(t, p.dir)}`);
export const geoPhotos = (reports) => (reports || []).flatMap((r) => (r.photos || []).filter((m) => m.lat != null && m.lon != null).map((m) => ({ ...m, repId: r.id })));

// position + direction for a photo from the report camera: live position (RTK / phone GPS) when just taken, else the report point
const phoneFix = () => new Promise((res) => { if (!navigator.geolocation) return res(null); navigator.geolocation.getCurrentPosition((x) => res([x.coords.latitude, x.coords.longitude]), () => res(null), { enableHighAccuracy: true, timeout: 6000, maximumAge: 15000 }); });
export async function photoPos(report, live) {
  const at = report?.lat != null ? { lat: report.lat, lon: report.lon } : {};
  if (!live) return at;
  const s = gnssState();
  const [p, dir] = await Promise.all([gnssLive(s) ? [s.lat, s.lon] : phoneFix(), readCompass()]);
  return { ...(p ? { lat: p[0], lon: p[1] } : at), ...(dir != null ? { dir: norm(dir), dirSrc: 'compass' } : {}) };
}

// direction the back camera looks at (° from north); phone lying flat -> direction of the top edge
const camHeading = (e) => {
  if (e.webkitCompassHeading != null) return e.webkitCompassHeading;
  if (e.alpha == null || !(e.absolute || e.type === 'deviceorientationabsolute')) return null;
  const r = Math.PI / 180, a = e.alpha * r, b = (e.beta || 0) * r, g = (e.gamma || 0) * r;
  const E = -(Math.cos(a) * Math.sin(g) + Math.sin(a) * Math.sin(b) * Math.cos(g));
  const N = Math.cos(a) * Math.sin(b) * Math.cos(g) - Math.sin(a) * Math.sin(g);
  if (Math.hypot(E, N) < 0.35) return (360 - e.alpha + (window.screen?.orientation?.angle || 0)) % 360;
  return (Math.atan2(E, N) / r + 360) % 360;
};

// capture direction while the camera is open: phone compass, else walking direction (ref only, no re-renders)
export function useCamDir(on) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = null;
    if (!on) return undefined;
    let buf = [], last = 0, comp = null, walk = null, trail = [];
    const pick = () => {
      const c = comp && Date.now() - comp.ts < 3000 ? comp : null;
      ref.current = c && !(c.bad && walk != null) ? { dir: norm(c.h), dirSrc: 'compass' } : walk != null ? { dir: norm(walk), dirSrc: 'walk' } : null;
    };
    const ev = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
    const onOri = (e) => {
      const v = camHeading(e), now = Date.now();
      if (v == null || now - last < 150) return;
      last = now; buf = [...buf.slice(-7), v]; comp = { h: v, bad: spread(buf) > 20, ts: now }; pick();
    };
    const onPos = (lat, lon) => {
      const p = [lat, lon];
      if (!trail.length || geoDist(trail[trail.length - 1], p) >= 0.5) trail = [...trail.slice(-5), p];
      const a = trail[0], b = trail[trail.length - 1];
      if (geoDist(a, b) >= 2) walk = bearingDeg((b[0] - a[0]) * mPerLat(a[0]), (b[1] - a[1]) * mPerLon(a[0]));
      pick();
    };
    const D = window.DeviceOrientationEvent;
    if (D && typeof D.requestPermission === 'function') D.requestPermission().catch(() => {});
    window.addEventListener(ev, onOri);
    const un = subscribeGnss(() => { const s = gnssState(); if (gnssLive(s)) onPos(s.lat, s.lon); });
    const id = navigator.geolocation?.watchPosition((x) => { if (!gnssLive(gnssState())) onPos(x.coords.latitude, x.coords.longitude); }, () => {}, { enableHighAccuracy: true, maximumAge: 1000 });
    return () => { window.removeEventListener(ev, onOri); un(); if (id != null) navigator.geolocation.clearWatch(id); };
  }, [on]);
  return ref;
}
